import { ReactElement } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { Col, Tile, uniqueId } from '@op/opds.opux';
import { color, font } from '@op/opds.tokens';
import { Message } from '@messageformat/react';
import ProfitRiskChartLabel from './ProfitRiskChartLabel';
import { getChartOptionsWithOverrides } from '../../../util/defaultOptions';
import { ProfitRiskGraph } from '../../../interfaces/ProfitRiskGraph';
import { formatNumberWithoutDecimals } from '../../../utils';

interface Props {
  title: string;
  data: ProfitRiskGraph;
  percentages: {
    x: number;
    y: number;
  };
  className: string;
  altText: string;
}

const ProfitRiskChart = (props: Props): ReactElement => {
  const { title, percentages, data, className, altText } = props;
  const options = getChartOptionsWithOverrides({
    accessibility: {
      enabled: false,
    },
    title: {
      text: '',
    },
    chart: {
      marginBottom: 20,
      height: 300,
    },
    rangeSelector: {
      enabled: false,
    },
    scrollbar: { enabled: false },
    plotOptions: {
      series: {
        pointStart: Date.UTC(2025, 4, 1),
        enableMouseTracking: false,
        marker: {
          enabled: false,
        },
      },
    },
    yAxis: {
      title: { text: undefined },
      showLastLabel: true,
      showFirstLabel: true,
      ceiling: 30000,
      max: 30000,
      tickInterval: 5000,
      opposite: false,
      labels: {
        enabled: true,
        allowOverlap: false,
        formatter: (label: Highcharts.AxisLabelsFormatterContextObject) => {
          return `${formatNumberWithoutDecimals(Number(label.value) ? Number(label.value) : 0)} €`;
        },
      },
    },
    xAxis: {
      lineWidth: 0,
      minorGridLineWidth: 0,
      type: 'datetime',
      minorTickLength: 0,
      tickLength: 0,
      tickInterval: 365 * 24 * 3600 * 1000 * 12,
      units: [['year', [1]]],
    },
    series: [
      {
        type: 'line',
        color: '#EE5A00',
        data: data.expected,
        zIndex: 1,
      },
      {
        name: 'Range',
        data: data.upper.map((value, index) => {
          return [value, data.lower[index]];
        }),
        type: 'arearange',
        lineWidth: 1,
        linkedTo: ':previous',
        color: color.icon.default,
        fillColor: '#e1e1e180',
        zIndex: 0,
        marker: {
          enabled: false,
        },
      },
    ],
  });
  const altTextId = uniqueId('profit_risk_chart_description');
  return (
    <Col md={6} xs={12} className={className}>
      <Tile style={{ marginLeft: 0, marginRight: 0 }}>
        <figure>
          <figcaption className="investoracademy-general-graph-title">
            <Message id={title} />
          </figcaption>
          <div role="img" aria-labelledby={altTextId}>
            <p className="investoracademy-general-graph-description">
              <Message id="course2.part1.profitAndRiskGraphs.title_subtitle" />
            </p>
            <div>
              <HighchartsReact
                highcharts={Highcharts}
                options={options}
                id="profit_risk_chart_text"
              />
              <div className="investoracademy-investorgraph-label-container">
                <p className="align-center" style={{ fontSize: font.size.body.xs }}>
                  2025
                </p>
                <p className="align-center" style={{ fontSize: font.size.body.xs }}>
                  2030
                </p>
                <p className="align-center" style={{ fontSize: font.size.body.xs }}>
                  2035
                </p>
              </div>
            </div>
            <ProfitRiskChartLabel percentages={percentages} />
            <p className="sr-only" id={altTextId}>
              {altText}
            </p>
          </div>
        </figure>
      </Tile>
    </Col>
  );
};

export default ProfitRiskChart;
